import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, Eye, Calendar, Share2, Twitter, Facebook, Linkedin, Copy, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import Navbar from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';

const API = `${window.location.origin}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/blog/posts/${slug}`);
        if (!res.ok) {
          setPost(null);
          setLoading(false);
          return;
        }
        const data = await res.json();
        setPost(data);

        // Record view
        fetch(`${API}/blog/posts/${slug}/view`, { method: 'POST' }).catch(() => {});

        // Fetch related posts from same category
        if (data.category_slug) {
          const relRes = await fetch(`${API}/blog/posts?category=${data.category_slug}&limit=3`);
          const relData = await relRes.json();
          setRelated((relData.posts || []).filter((p) => p.slug !== slug).slice(0, 2));
        }
      } catch (e) {
        console.error('Blog post fetch error:', e);
      }
      setLoading(false);
    };
    fetchPost();
    window.scrollTo(0, 0);
  }, [slug]);

  const shareUrl = `${window.location.origin}/blog/${slug}`;
  const shareText = post?.title || '';

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* fallback: do nothing */ }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // JSON-LD structured data
  const jsonLd = post ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.meta_title || post.title,
    "description": post.meta_description || post.excerpt,
    "image": post.og_image || post.featured_image || '',
    "author": {
      "@type": post.author_type === 'ai_agent' ? 'Organization' : 'Person',
      "name": post.author_name,
    },
    "datePublished": post.published_at,
    "dateModified": post.updated_at,
    "publisher": {
      "@type": "Organization",
      "name": "Blackified",
      "url": window.location.origin,
    },
    "mainEntityOfPage": shareUrl,
    "wordCount": post.content?.split(/\s+/).length || 0,
  } : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="pt-24 px-6 max-w-3xl mx-auto animate-pulse space-y-6">
          <div className="h-6 bg-zinc-800 rounded w-1/4" />
          <div className="h-10 bg-zinc-800 rounded w-3/4" />
          <div className="h-64 bg-zinc-800 rounded" />
          <div className="space-y-3">
            {[...Array(8)].map((_, i) => <div key={i} className="h-4 bg-zinc-800 rounded" style={{ width: `${70 + Math.random() * 30}%` }} />)}
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="pt-24 px-6 text-center">
          <h1 className="text-3xl font-bold text-white mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-zinc-400 hover:text-white underline">Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.meta_title || post.title} - Blackified Blog</title>
        <meta name="description" content={post.meta_description || post.excerpt} />
        <meta property="og:title" content={post.meta_title || post.title} />
        <meta property="og:description" content={post.meta_description || post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={shareUrl} />
        {(post.og_image || post.featured_image) && <meta property="og:image" content={post.og_image || post.featured_image} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.meta_title || post.title} />
        <meta name="twitter:description" content={post.meta_description || post.excerpt} />
        <link rel="canonical" href={shareUrl} />
        {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
      </Helmet>

      <div className="min-h-screen bg-[#050505]">
        <Navbar />

        <article className="pt-24 pb-20 px-6">
          <div className="max-w-3xl mx-auto">
            {/* Back link */}
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-zinc-500 hover:text-white text-sm mb-4 transition-colors"
              data-testid="blog-back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>

            {/* Category */}
            {post.category_name && (
              <Link
                to={`/blog?category=${post.category_slug}`}
                className="block text-xs font-medium uppercase tracking-wider text-zinc-500 hover:text-white mb-3 transition-colors"
                data-testid="blog-post-category"
              >
                {post.category_name}
              </Link>
            )}

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight"
              data-testid="blog-post-title"
            >
              {post.title}
            </motion.h1>

            {/* Meta bar */}
            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-zinc-500">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {formatDate(post.published_at)}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {post.reading_time} min read
              </span>
              {post.views_count > 0 && (
                <span className="flex items-center gap-1.5">
                  <Eye className="w-4 h-4" />
                  {post.views_count.toLocaleString()} views
                </span>
              )}
              <span>By {post.author_name}</span>
            </div>

            {/* Featured image */}
            {post.featured_image && (
              <div className="mt-8 rounded-xl overflow-hidden border border-zinc-800">
                <img
                  src={post.featured_image}
                  alt={post.title}
                  className="w-full max-h-[480px] object-cover"
                  data-testid="blog-post-image"
                />
              </div>
            )}

            {/* Content */}
            <div className="mt-10 prose prose-invert prose-zinc max-w-none
              prose-headings:text-white prose-headings:font-bold
              prose-p:text-zinc-300 prose-p:leading-relaxed
              prose-a:text-white prose-a:underline prose-a:underline-offset-4
              prose-strong:text-white
              prose-code:bg-zinc-800 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
              prose-pre:bg-zinc-900 prose-pre:border prose-pre:border-zinc-800 prose-pre:rounded-lg
              prose-img:rounded-lg prose-img:border prose-img:border-zinc-800
              prose-blockquote:border-zinc-700 prose-blockquote:text-zinc-400
              prose-li:text-zinc-300
              prose-table:text-zinc-300
              prose-th:text-white prose-th:border-zinc-700
              prose-td:border-zinc-800"
              data-testid="blog-post-content"
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                {post.content}
              </ReactMarkdown>
            </div>

            {/* Tags */}
            {post.tags?.length > 0 && (
              <div className="mt-10 flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-zinc-900 border border-zinc-800 text-xs text-zinc-400">
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Share */}
            <div className="mt-10 pt-8 border-t border-zinc-800">
              <p className="text-sm text-zinc-500 mb-3">Share this article</p>
              <div className="flex items-center gap-3">
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700 transition-colors"
                  data-testid="share-twitter"
                >
                  <Twitter className="w-4 h-4" />
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700 transition-colors"
                  data-testid="share-facebook"
                >
                  <Facebook className="w-4 h-4" />
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700 transition-colors"
                  data-testid="share-linkedin"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
                <button
                  onClick={handleCopyLink}
                  className="p-2.5 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700 transition-colors"
                  data-testid="share-copy"
                >
                  {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Related posts */}
            {related.length > 0 && (
              <div className="mt-12 pt-8 border-t border-zinc-800">
                <h3 className="text-lg font-semibold text-white mb-6">Related Articles</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {related.map((r) => (
                    <Link
                      key={r.post_id}
                      to={`/blog/${r.slug}`}
                      className="group block p-4 rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-colors"
                    >
                      <span className="text-xs text-zinc-600">{r.category_name}</span>
                      <h4 className="mt-1 text-sm font-medium text-white group-hover:text-zinc-200 line-clamp-2">
                        {r.title}
                      </h4>
                      <span className="mt-2 text-xs text-zinc-600">{r.reading_time} min read</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </div>
    </>
  );
}
